import React, { useState } from "react";
import { Shield, CreditCard } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NexiPaymentButton from "@/components/NexiPaymentButton";

/**
 * Pagina "Paga la tua polizza" - checkout universale.
 * Accessibile da /paga?importo=150&tipo=auto&email=...&num_contratto=...
 * oppure come pagina pubblica dove il cliente inserisce i dati a mano.
 */
export default function PagaPolizzaPage() {
  const qp = new URLSearchParams(window.location.search);
  const [amount, setAmount] = useState(() => {
    const v = parseFloat(qp.get("importo") || "");
    return Number.isFinite(v) && v > 0 ? v : "";
  });
  const [email, setEmail] = useState(qp.get("email") || "");
  const [name, setName] = useState(qp.get("nome") || "");
  const [tipoPolizza, setTipoPolizza] = useState(qp.get("tipo") || "generico");
  const [numContratto, setNumContratto] = useState(qp.get("num_contratto") || "");
  const [description, setDescription] = useState(qp.get("descrizione") || "");

  const amt = parseFloat(amount);
  const canPay = Number.isFinite(amt) && amt > 0 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-20">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Paga la tua polizza</h1>
              <p className="text-slate-400 text-sm mt-0.5">
                Pagamento sicuro tramite circuito Nexi XPay
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-md p-8 space-y-5">
            <Field label="Importo polizza (€)" required>
              <input
                type="number"
                step="0.01"
                min="0.50"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="es. 150.00"
                className="w-full bg-black/40 border border-white/10 focus:border-amber-400 rounded-xl px-4 py-3 text-white outline-none transition-colors"
                data-testid="paga-amount-input"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nome e cognome">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Mario Rossi"
                  className="w-full bg-black/40 border border-white/10 focus:border-amber-400 rounded-xl px-4 py-3 text-white outline-none transition-colors"
                  data-testid="paga-name-input"
                />
              </Field>

              <Field label="Email" required>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="mario.rossi@email.it"
                  className="w-full bg-black/40 border border-white/10 focus:border-amber-400 rounded-xl px-4 py-3 text-white outline-none transition-colors"
                  data-testid="paga-email-input"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Tipo polizza">
                <select
                  value={tipoPolizza}
                  onChange={(e) => setTipoPolizza(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 focus:border-amber-400 rounded-xl px-4 py-3 text-white outline-none transition-colors"
                  data-testid="paga-tipo-select"
                >
                  <option value="generico">Generico</option>
                  <option value="auto">RC Auto</option>
                  <option value="casa">Casa</option>
                  <option value="vita">Vita</option>
                  <option value="viaggio">Viaggio</option>
                  <option value="nlt">Noleggio Lungo Termine</option>
                  <option value="professionale">Professionale</option>
                  <option value="business">Business</option>
                </select>
              </Field>

              <Field label="Numero contratto (opzionale)">
                <input
                  type="text"
                  value={numContratto}
                  onChange={(e) => setNumContratto(e.target.value)}
                  placeholder="es. 2026/0001"
                  className="w-full bg-black/40 border border-white/10 focus:border-amber-400 rounded-xl px-4 py-3 text-white outline-none transition-colors"
                  data-testid="paga-contratto-input"
                />
              </Field>
            </div>

            <Field label="Note / descrizione (opzionale)">
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="es. Rinnovo annuale 2026"
                className="w-full bg-black/40 border border-white/10 focus:border-amber-400 rounded-xl px-4 py-3 text-white outline-none transition-colors"
                data-testid="paga-description-input"
              />
            </Field>

            <div className="pt-4">
              <NexiPaymentButton
                amountEur={amt || 0}
                description={description || "Pagamento polizza Colucci Assicuratori"}
                email={email}
                customerName={name}
                tipoPolizza={tipoPolizza}
                numContratto={numContratto}
                disabled={!canPay}
              />
            </div>

            <p className="text-xs text-slate-500 flex items-start gap-2 pt-3 border-t border-white/5">
              <Shield className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Dati trasmessi in modalità cifrata. Colucci Assicuratori non memorizza né tratta dati di carta di
                credito: tutti i pagamenti sono gestiti da Nexi XPay, certificata PCI-DSS Level 1.
              </span>
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-200 mb-1.5 block">
        {label}
        {required && <span className="text-amber-400 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}
